import {
  DownloadReport,
  ReportPreview,
  CustomQueryDto,
} from "../@types/reporting";
import axios from "../utils/axios";

export class ReportingService {
  // Download report with actual file download
  async downloadReport(data: DownloadReport): Promise<void> {
    try {
      const response = await axios.post("/reports/download", data, {
        responseType: "blob", // Important for file downloads
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Get filename from response headers or generate one
      const contentDisposition = response.headers["content-disposition"];
      let filename = `report.${data.format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      throw error;
    }
  }

  // Track report download (for analytics)
  trackReportDownload(data: Omit<DownloadReport, "format">) {
    return axios.post("/reports/track-download", data);
  }

  // Preview report data
  async previewReport(
    data: Omit<DownloadReport, "format">
  ): Promise<ReportPreview> {
    const response = await axios.post("/reports/preview", data);
    return response.data;
  }

  // Get download history
  getDownloadHistory() {
    return axios.get(`/reports/download-history`);
  }

  // Start asynchronous report generation
  async asyncDownloadReport(data: DownloadReport): Promise<any> {
    const response = await axios.post('/reports/async-download', data);
    return response.data;
  }

  // Check status of a report task
  async getTaskStatus(taskId: number): Promise<any> {
    const response = await axios.get(`/reports/task-status/${taskId}`);
    return response.data;
  }

  // Execute custom query synchronously
  async customQuery(data: CustomQueryDto): Promise<any> {
    const response = await axios.post('/reports/custom-query', data);
    return response.data;
  }

  // Start background custom query
  async asyncCustomQuery(data: CustomQueryDto): Promise<any> {
    const response = await axios.post('/reports/async-custom-query', data);
    return response.data;
  }

  // Helper to trigger download from a result URL
  triggerDownload(resultUrl: string, fileName: string) {
    // In a real scenario, this would be a full S3 URL or an API proxy
    // For now, we'll assume it's a relative URL
    const link = document.createElement('a');
    link.href = resultUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async getDashboardStats(): Promise<any> {
    const response = await axios.get('/reports/dashboard-stats');
    return response.data;
  }
}

export const reportingService = new ReportingService();
